import { useCallback, useEffect, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import AppConfig from '@/AppConfig';
import toast from 'react-hot-toast';
import setupLocationMethods from '@/utils/nativeLocationMethods';

interface IOptions {
  runOnMount?: boolean;
}

export type TCoords = {
  latitude: number;
  longitude: number;
} | null;

const useLocation = ({ runOnMount }: IOptions = { runOnMount: true }) => {
  const { isWebView } = useAppContext();
  const [isFetchingLocation, setFetchingLocation] = useState(true);
  const [isLocationRejected, setLocationRejected] = useState(false);

  const [location, setLocation] = useState<TCoords>(null);

  const showPermissionToast = () =>
    toast("Please enable location access in your browser's settings", {
      id: 'location-permission-toast',
      style: {
        width: '100%',
        maxWidth: '480px',
        textAlign: 'center',
      },
      duration: 5000,
    });

  const queryLocationForWeb = useCallback(() => {
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        setFetchingLocation(false);
        setLocationRejected(false);
      },
      (error) => {
        setFetchingLocation(false);

        if (error.code === error.PERMISSION_DENIED) {
          setLocationRejected(true);
          showPermissionToast();
        }
      }
    );
  }, []);

  const clearLocation = () => {
    setLocation(null);
  };

  const getPermissions = () => {
    if (isWebView) {
      window?.NativeBridge?.openSettingsForLocationPermission?.();
    } else {
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'granted') {
            setFetchingLocation(false);
            setLocationRejected(false);
            queryLocationForWeb();
          } else if (result.state === 'prompt') {
            setFetchingLocation(false);
            if (isLocationRejected) {
              showPermissionToast();
            } else {
              setLocationRejected(true);
            }
          } else if (result.state === 'denied') {
            setFetchingLocation(false);
            setLocationRejected(true);
            showPermissionToast();
          }
        });
      } else {
        window.dev.e("Permissions API not supported")
      }
    }
  };

  const getLocation = (init?: boolean) => {
    window.dev.i(
      'useLocation: ',
      'getLocation called',
      isLocationRejected,
      init
    );
    setFetchingLocation(true);
    if (isLocationRejected && !init) {
      getPermissions();
    } else {
      if (isWebView) {
        window?.NativeBridge?.getLocation?.();
      } else {
        queryLocationForWeb();
      }
    }
  };

  const toggleLocation = () => {
    if (location) {
      clearLocation();
    } else {
      getLocation();
    }
  };

  const initLocation = () => {
    setupLocationMethods();
    getLocation(true);
  };

  useEffect(() => {
    if (runOnMount) {
      initLocation();
    }
  }, [runOnMount]);

  useEffect(() => {
    const handleRequestRejected = () => {
      setLocationRejected(true);
      setFetchingLocation(false);
    };

    const handleLocationUpdated = (event: Event) => {
      setLocation((event as CustomEvent).detail);
      setFetchingLocation(false);
      setLocationRejected(false);
      window?.NativeBridge?.stopReceivingLocationUpdates?.();
    };

    window.addEventListener(
      AppConfig.LOCATION_REQUEST_REJECTED_EVENT,
      handleRequestRejected
    );

    window.addEventListener(
      AppConfig.LOCATION_UPDATED_EVENT,
      handleLocationUpdated
    );

    return () => {
      window.removeEventListener(
        AppConfig.LOCATION_REQUEST_REJECTED_EVENT,
        handleRequestRejected
      );
      window.removeEventListener(
        AppConfig.LOCATION_UPDATED_EVENT,
        handleLocationUpdated
      );
    };
  }, []);

  return {
    location,
    isFetchingLocation,
    isLocationRejected,
    queryLocationForWeb,
    clearLocation,
    getLocation,
    toggleLocation,
    initLocation,
  };
};

export default useLocation;
